import { PhoneCaseModel } from './Phone';
import { IUser } from './UserModel';

export enum ProductType {
  sticker = 'sticker',
  magnet_sticker = 'magnet_sticker',
  iphone = 'iphone',
  poster = 'poster',
  uvdtf = 'uvdtf',
  badge_reel = 'badge_reel',
  glass_suncatcher = 'glass_suncatcher',
  squishy_belly = 'squishy_belly',
  card_skin = 'card_skin',
  ornament = 'ornament',
}

// Orthogonal to OrderType — persisted per-design on the backend. Used by
// products that come in distinct hardware/shape variants (badge reel clip
// type, glass suncatcher shape, etc.). Backend rejects:
//   - products with variants that submit a design without one, or
//   - products without variants that submit a design with one.
export enum ProductVariant {
  // Badge Reel
  BeltClip = 'belt_clip',
  SwivelClip = 'swivel_clip',
  Topper = 'topper',

  // Glass Suncatcher
  Round = 'round',
  Oval = 'oval',
  Heart = 'heart',

  // Squishy Belly
  Pack1 = 'pack_1',
  Pack2 = 'pack_2',
  Pack3 = 'pack_3',
  Pack5 = 'pack_5',

  // Card Skin
  Standard = 'standard',
  Holographic = 'holographic',

  // Ornament
  Ceramic = 'ceramic',
  Glass = 'glass',
  Acrylic = 'acrylic',
}

export const ProductVariantLabelMap: Record<ProductVariant, string> = {
  [ProductVariant.BeltClip]: 'Belt Clip',
  [ProductVariant.SwivelClip]: 'Swivel Clip',
  [ProductVariant.Topper]: 'Topper',
  [ProductVariant.Round]: 'Round',
  [ProductVariant.Oval]: 'Oval',
  [ProductVariant.Heart]: 'Heart',
  [ProductVariant.Pack1]: 'Pack 1',
  [ProductVariant.Pack2]: 'Pack 2',
  [ProductVariant.Pack3]: 'Pack 3',
  [ProductVariant.Pack5]: 'Pack 5',
  [ProductVariant.Standard]: 'Standard',
  [ProductVariant.Holographic]: 'Holographic',
  [ProductVariant.Ceramic]: 'Ceramic',
  [ProductVariant.Glass]: 'Glass',
  [ProductVariant.Acrylic]: 'Acrylic',
};

export enum RequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum OrderStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  RESHIPPED = 'reshipped',
  CANCELLED = 'cancelled',
}

export interface ISizeVariantPrice {
  id: string;
  variant: ProductVariant | null;
  price: number;
  isOutOfStock?: boolean | null;
}

export interface ISize {
  id?: string;
  description?: string;
  size: string;
  price: number;
  product: ProductType;
  numericalOrder?: number;
  updatedAt?: string;
  // Soft "do not sell" flag controlled by admins. Treat anything falsy
  // (false / null / undefined) as in-stock; only an explicit `true` means
  // out of stock. The backend still returns out-of-stock sizes from the list
  // endpoint — the FE is responsible for disabling them in pickers.
  isOutOfStock?: boolean | null;
  variantPrices?: ISizeVariantPrice[];
}

export interface IDesign {
  id?: string;
  sku?: string;
  design?: any;
  designUrl?: string;
  createdAt?: string;
  isSelected?: boolean;
  originalFileUrl?: string;
  thumbnailUrl?: string;
  caseModel?: PhoneCaseModel;
  isGeneratingThumbnail?: boolean;
}

export interface IOrderItem {
  id?: string;
  sizeId?: string;
  designId?: string;
  quantity: number;
  itemCost?: number;
  product?: ProductType;
  // Required for products where `productHasVariants(orderType)` is true
  // (badge_reel, glass_suncatcher). Must be omitted for everything else.
  variant?: ProductVariant;
}

export interface IOrderItemResponse {
  design?: IDesign;
  id?: string;
  quantity?: number;
  size: ISize;
  itemCost?: number;
  // null when the product has no variants; otherwise one of ProductVariant.
  variant?: ProductVariant | null;
}

export interface IRefundRequest {
  id: string;
  reason?: string;
  evidence: string;
  status: RequestStatus;
  requestedAt: string;
  updatedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
}

export interface IResendRequest {
  id: string;
  reason?: string;
  status: RequestStatus;
  requestedAt: string;
  updatedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
}

export interface IOrder {
  id?: string;
  createdAt?: string;
  lastStatusUpdatedAt?: string;
  orderId?: string;
  address?: string;
  country?: 'ca' | 'us';
  trackingReference?: string;
  designs?: IOrderItem[];
  orderDesigns?: IOrderItemResponse[];
  totalCost?: number;
  sellerId?: string;
  status?: OrderStatus;
  seller?: IUser;
  storeId?: string | null;
  store?: { id: string; name: string; isDefault?: boolean } | null;
  refundRequest?: IRefundRequest;
  resendRequest?: IResendRequest;
  refundRequests?: IRefundRequest[];
  resendRequests?: IResendRequest[];
  type?: OrderType;

  // Shipping label related fields
  shippingLabelUrl?: string;
  shippingCarrier?: string;
  shippingService?: string;
  estimatedDeliveryDate?: string;
  shippingCost?: number;
  /** Shippo label purchase fee (from Shippo rate), set on admin Shippo buy. */
  labelFee?: number;
  /** Platform the label was bought on ('easypost' | 'shippo'). */
  labelProvider?: string;
  trackingUrl?: string;
  shipmentId?: string;
  shipmentStatus?: string;

  // Shipping label creation option
  createShippingLabel?: boolean;

  // Request admin to purchase shipment
  requestAdminPurchaseShipment?: boolean;

  shipWithHardTube?: boolean;

  notes?: string;

  /**
   * When true, country shipping fee stays baked into totalCost even if a
   * trackingReference exists (e.g. after EasyPost label purchase).
   */
  shouldLockTotalCost?: boolean;
}

export const enum SortOption {
  ASC = 'ASC',
  DESC = 'DESC',
}

export const ResendRequestStatusLabelMap = {
  [RequestStatus.APPROVED]: 'Resend Approved',
  [RequestStatus.PENDING]: 'Pending Resend',
  [RequestStatus.REJECTED]: 'Resend Rejected',
};

export enum OrderType {
  Sticker = 'sticker',
  MagnetSticker = 'magnet_sticker',
  PhoneCase = 'phone_case',
  Poster = 'poster',
  UVDTF = 'uvdtf',
  BadgeReel = 'badge_reel',
  GlassSuncatcher = 'glass_suncatcher',
  SquishyBelly = 'squishy_belly',
  CardSkin = 'card_skin',
  Ornament = 'ornament',
}

export const ProductOrderTypeMap: Record<ProductType, OrderType> = {
  iphone: OrderType.PhoneCase,
  sticker: OrderType.Sticker,
  magnet_sticker: OrderType.MagnetSticker,
  poster: OrderType.Poster,
  uvdtf: OrderType.UVDTF,
  badge_reel: OrderType.BadgeReel,
  glass_suncatcher: OrderType.GlassSuncatcher,
  squishy_belly: OrderType.SquishyBelly,
  card_skin: OrderType.CardSkin,
  ornament: OrderType.Ornament,
};

// OrderTypes that share Poster's behaviors (hard-tube shipping option,
// dedicated upload flow, etc.). Use the helpers below instead of
// `type === 'poster'` checks anywhere those behaviors apply.
export const POSTER_LIKE_ORDER_TYPES = [
  OrderType.Poster,
  OrderType.BadgeReel,
  OrderType.GlassSuncatcher,
  OrderType.SquishyBelly,
  OrderType.Ornament,
] as const;

export const isPosterLikeOrderType = (t?: OrderType | null): boolean =>
  !!t &&
  POSTER_LIKE_ORDER_TYPES.includes(
    t as (typeof POSTER_LIKE_ORDER_TYPES)[number],
  );

export const POSTER_LIKE_PRODUCT_TYPES = [
  ProductType.poster,
  ProductType.badge_reel,
  ProductType.glass_suncatcher,
  ProductType.squishy_belly,
  ProductType.ornament,
] as const;

export const isPosterLikeProductType = (p?: ProductType | null): boolean =>
  !!p &&
  POSTER_LIKE_PRODUCT_TYPES.includes(
    p as (typeof POSTER_LIKE_PRODUCT_TYPES)[number],
  );

// Poster-like products that offer the optional hard-tube shipping toggle.
export const HARD_TUBE_PRODUCT_TYPES = [
  ProductType.poster,
  ProductType.badge_reel,
  ProductType.glass_suncatcher,
] as const;

export const supportsHardTubeShipping = (p?: ProductType | null): boolean =>
  !!p &&
  HARD_TUBE_PRODUCT_TYPES.includes(
    p as (typeof HARD_TUBE_PRODUCT_TYPES)[number],
  );

// Which OrderTypes accept (and require) a per-design `variant` field.
// Keep this in sync with the backend's ProductVariantsService.
export const ProductVariantsMap: Partial<Record<OrderType, ProductVariant[]>> =
  {
    [OrderType.BadgeReel]: [
      ProductVariant.BeltClip,
      ProductVariant.SwivelClip,
      ProductVariant.Topper,
    ],
    [OrderType.GlassSuncatcher]: [
      ProductVariant.Round,
      ProductVariant.Oval,
      ProductVariant.Heart,
    ],
    [OrderType.SquishyBelly]: [
      ProductVariant.Pack1,
      ProductVariant.Pack2,
      ProductVariant.Pack3,
      ProductVariant.Pack5,
    ],
    [OrderType.CardSkin]: [
      ProductVariant.Standard,
      ProductVariant.Holographic,
    ],
    [OrderType.Ornament]: [
      ProductVariant.Ceramic,
      ProductVariant.Glass,
      ProductVariant.Acrylic,
    ],
  };

export const productHasVariants = (t?: OrderType | null): boolean =>
  !!t && (ProductVariantsMap[t]?.length ?? 0) > 0;

export const productHasVariantsByProductType = (
  p?: ProductType | null,
): boolean => productHasVariants(p ? ProductOrderTypeMap[p] : undefined);

export const getProductVariants = (t?: OrderType | null): ProductVariant[] =>
  t ? (ProductVariantsMap[t] ?? []) : [];

export const getProductVariantsByProductType = (
  p?: ProductType | null,
): ProductVariant[] =>
  p ? (ProductVariantsMap[ProductOrderTypeMap[p]] ?? []) : [];

// OrderTypes that require a non-empty `trackingReference` on create/update.
// The FE should mark the field as required and block submit; the backend
// will 400 with a clear message otherwise.
export const ORDER_TYPES_REQUIRING_TRACKING = [
  OrderType.GlassSuncatcher,
] as const;

export const requiresTrackingReference = (t?: OrderType | null): boolean =>
  !!t &&
  ORDER_TYPES_REQUIRING_TRACKING.includes(
    t as (typeof ORDER_TYPES_REQUIRING_TRACKING)[number],
  );

export const requiresTrackingReferenceByProductType = (
  p?: ProductType | null,
): boolean => requiresTrackingReference(p ? ProductOrderTypeMap[p] : undefined);

// Products where variant selection changes unit price (squishy belly pack
// size, card skin standard/holographic, ornament ceramic/glass). Other
// variant products still use size.price for cart preview.
export const VARIANT_PRICING_PRODUCT_TYPES = [
  ProductType.squishy_belly,
  ProductType.card_skin,
  ProductType.ornament,
] as const;

export const productUsesVariantPricing = (p?: ProductType | null): boolean =>
  !!p &&
  VARIANT_PRICING_PRODUCT_TYPES.includes(
    p as (typeof VARIANT_PRICING_PRODUCT_TYPES)[number],
  );

export const resolveUnitPrice = (
  size: ISize,
  variant?: ProductVariant | null,
): number | null => {
  const rows = size.variantPrices ?? [];

  if (variant) {
    const exact = rows.find((r) => r.variant === variant);
    if (exact) return exact.price;
  }

  const fallback = rows.find((r) => r.variant == null);
  if (fallback) return fallback.price;

  return size.price ?? null;
};

export const isLineAvailable = (
  size: ISize,
  variant?: ProductVariant | null,
): boolean => {
  if (size.isOutOfStock) return false;

  if (variant) {
    const row = size.variantPrices?.find((r) => r.variant === variant);
    if (row?.isOutOfStock) return false;
  }

  return true;
};

export const getVariantSelectOptions = (
  size: ISize | undefined,
  productType: ProductType,
): { label: string; value: ProductVariant; disabled?: boolean }[] =>
  getProductVariantsByProductType(productType).map((v) => {
    const row = size?.variantPrices?.find((r) => r.variant === v);
    const oos = !!row?.isOutOfStock;
    return {
      label: oos
        ? `${ProductVariantLabelMap[v]} (out of stock)`
        : ProductVariantLabelMap[v],
      value: v,
      disabled: oos,
    };
  });
